// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import api from '../../../../common/api';
import config from '../../../../share/config';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';

export default {
  name: "readinessCodePage",
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      measuredDataItems: [],
    };
  },

  watch: {
    language() {
      this.updateColumns();
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * Readiness Code計測データ一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     */
    async createReadinessCodeMeasuresList(projectId, measuresId, system) {
      try {
        this.processing = true;
        const reproEndPoint = '/diagDtc/' + projectId + '/' + measuresId + '/readinessCode/' + system;
        const params = { region_info: this.cachedProjectDataSource };
        const resRepro = await api.getParamCall(config.DIAG, reproEndPoint, params, this.operationScreen);

        handler.validate(handler.validateTypes.all, resRepro, this, null, () => {
          this.recollectedData = resRepro.data;
          this.updateColumns();
          this.measuredDataItems = this.getReadinessTable(resRepro.data.readiness_result);
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定 */
    async updateColumns() {
      this.measuredDataColumns = [
        { name: 'dtc', label: 'individual.header_readiness_code_dtc', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'result', label: 'individual.header_readiness_code_result', useResource: true },
      ];
    },

    /**
     * レディネスコードの一覧表示.
     * @param {Array} detailData レスポンスのデータ
     * @returns レディネスコードの計測データ一覧
     */
    getReadinessTable(detailData) {
      const param = [];
      if (!detailData || detailData.length < 1) {
        return param;
      }
      return detailData.map(d => ({
        dtc: d.code,
        result: d.readiness_status ? 'individual.label_diagnostic_incomplete' : 'individual.label_diagnostic_complete',
        bgStyle: 'dtc-cursor-default',
        description:''  // DTCの一覧ソートで必要なため追加
      }));
    },

    /**
     * ファイルエクスポートボタン押下.
     * DataRecollection.jsのcsvExportを呼び出している
     * csvExportに一覧のカラム、Itemを引数として渡している
     */
    csvExport() {
      this.$emit('csvExport', this.measuredDataColumns, this.measuredDataItems, this.recollectedData);
    },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    /**
     * 診断系のAPIは複数画面から使用されているため
     * 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
     * @returns 使用元の機能
     */
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};